import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { FieldsLogicService } from '.';
import { App, FilterMenuOptions, MapFilter, Project } from '../../models';
import { FieldType } from '../../shared/enums';
import { MapFiltersDataService } from '../data-services';
import { DragAndDropFieldSelectionUtilityService } from '../utility-services';
import { Field, MapFilterSelectionModel } from './models';

@Injectable({
    providedIn: 'root'
})

export class MapFiltersSelectionLogicService {
    private _mapFiltersValuesLoaded = new Subject<boolean>();

    mapFiltersValuesLoaded$ = this._mapFiltersValuesLoaded.asObservable();

    model: MapFilterSelectionModel;

    get mapFiltersForm(): UntypedFormGroup {
        return this.model.mapFiltersForm;
    }

    get availableFields(): Field[] {
        return this.model.availableFields;
    }

    set availableFields(inAvailableFields: Field[]) {
        this.model.availableFields = inAvailableFields;
    }

    get allFields(): Field[] {
        return this.model.mapFilterMenuOptions.allFields;
    }

    get selectedFields(): Field[] {
        return this.model.mapFilterMenuOptions.selectedFields;
    }

    set selectedFields(inSelectFields: Field[]) {
        if (inSelectFields?.length) {
            this.model.mapFilterMenuOptions.selectedFields = [...inSelectFields];
            if (this.allFields?.length) {
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.mapFilterMenuOptions.selectedFields, this.allFields);
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.mapFilterMenuOptions.selectedFields, this.model.availableFields);
                this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;
            }
            else {
                this.model.availableFields = [];
            }
        }
        else {
            this.model.availableFields = [...this.allFields];
            this.model.mapFilterMenuOptions.selectedFields = [];
            this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;
        }

        this.model.mapFiltersForm.patchValue({
            selectedFields: inSelectFields,
        });
    }

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _fieldsLogicService: FieldsLogicService,
        private _dragAndDropFieldSelectionUtilityService: DragAndDropFieldSelectionUtilityService,
        private _mapFiltersDataService: MapFiltersDataService,
        private _snackBar: MatSnackBar,
    ) { }

    initModel(): void {
        this.model = new MapFilterSelectionModel();
        this.model.id = null;
        this.model.isLoading = false;
        this.model.projectId = null;
        this.model.mapFilterMenuOptions = new FilterMenuOptions();
        this.model.mapFilterMenuOptions.selectedFields = Array<Field>();
        this.model.availableFields = Array<Field>();
        this.model.mapFilterMenuOptions.allFields = Array<Field>();
        this.model.mapFiltersForm = this._formBuilder.group({
            id: new UntypedFormControl(),
            projectId: new UntypedFormControl(),
            selectedFields: ([])
        });
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = [];
    }

    loadModel(incomingFilter: MapFilter): void {
        this.model.id = incomingFilter.id;
        this.model.projectId = incomingFilter.projectId;
        this.selectedFields = incomingFilter.selectedFields;
    }

    loadForm(): void {
        this.model.mapFiltersForm.patchValue({
            id: this.model.id,
            projectId: this.model.projectId,
            selectedFields: this.model.mapFilterMenuOptions.selectedFields
        });
    }

    getMapFilterByProjectId(app: App, project: Project): void {
        if (app.id == this.model.previousApp?.id && project.id == this.model.previousProject?.id) {
            return;
        }
        this.isModelLoading(true);
        this.model.previousApp = app;
        this.model.previousProject = project;
        this._mapFiltersDataService.getMapFilterByProjectId(app, project)
            .pipe(
                switchMap((mapFilter: MapFilter) => {
                    if (mapFilter != null) {
                        this.loadModel(mapFilter);
                        this.loadForm();
                        return this._fieldsLogicService.getGridFieldsToFields(app, project);
                    }
                    else {
                        this.initModel();
                        this.model.projectId = project.id;
                        this.selectedFields = new Array<Field>();
                        this.mapFiltersForm.patchValue({
                            projectId: project.id,
                        });
                        return this._fieldsLogicService.getGridFieldsToFields(app, project);
                    }
                })
            )
            .subscribe({
                next: (unfilteredFields: Field[]) => {
                    this.setAllFields(this.filterFieldsByMapFilter(unfilteredFields), app.aliases.site.singular);
                    if (this.selectedFields.length > 0) {
                        this.getMapFilterValuesByProjectId(app, project, this.selectedFields);
                    }
                    else {
                        this._mapFiltersValuesLoaded.next(true);
                    }

                    this._snackBar.open(`Map Filters loaded`, `Close`, { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error getting Map Filters`, `Close`, { duration: 5000 });
                }
            });
    }

    getMapFilterValuesByProjectId(app: App, project: Project, allFields: Field[]): void {
        this.isModelLoading(true);
        const mapFilterValues$ = project.isSummaryProject
            ? this._mapFiltersDataService.getMapFilterValuesBySummaryProjectId(app, project, allFields)
            : this._mapFiltersDataService.getMapFilterValuesByProjectId(app, project, allFields);
        mapFilterValues$
            .subscribe({
                next: (mapFilterMenuOptions: FilterMenuOptions) => {
                    if (mapFilterMenuOptions) {
                        this.model.mapFilterMenuOptions = {
                            ...this.model.mapFilterMenuOptions,
                            filterValues: {
                                ...this.model.mapFilterMenuOptions.filterValues,
                                ...mapFilterMenuOptions.filterValues
                            }
                        };
                    }
                },
                error: () => {
                    this._snackBar.open(`Error getting Map Filter Values`, `Close`, { duration: 5000 });
                }
            })
            .add(() => {
                this._mapFiltersValuesLoaded.next(true);
            });
    }

    saveMapFilters(app: App, project: Project, mapFilter: UntypedFormGroup): void {
        this._mapFiltersDataService.saveMapFilters(app, project, mapFilter)
            .subscribe({
                next: (savedFilter: MapFilter) => {
                    if (savedFilter?.id) {
                        this.loadModel(savedFilter);
                        this.loadForm();
                    }
                    this._snackBar.open(`Map Filters Saved`, `Close`, { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error saving Map Filters`, `Close`, { duration: 5000 });
                }
            });
    }

    filterFieldsByMapFilter(unfilteredFields: Field[]): Field[] {
        if (!unfilteredFields) {
            unfilteredFields = new Array<Field>();
        }

        const filteredFields: Field[] = [];

        unfilteredFields.forEach(field => {
            if (field.FieldType.id == FieldType.CheckBox ||
                field.FieldType.id == FieldType.DropDown ||
                field.FieldType.id == FieldType.Number ||
                field.FieldType.id == FieldType.DateSelect ||
                field.FieldType.id == FieldType.PointCategory ||
                field.FieldType.id == FieldType.Formula) {
                filteredFields.push(field);
            }
        });

        return filteredFields;
    }

    setDefaultFilters(allIncomingFields: Field[], siteAlias: string): Field[] {
        if (!allIncomingFields) {
            allIncomingFields = new Array<Field>();
        }

        const Field2: Field = { id: "4", Name: `${siteAlias} City`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Text Box', required: null, type: 'string' } };
        const Field3: Field = { id: "5", Name: `${siteAlias} State`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Text Box', required: null, type: 'string' } };
        const Field5: Field = { id: "7", Name: `${siteAlias} Status`, Alias: "", breadcrumb: "", FieldType: { id: `1`, text: 'Checkbox', required: null, type: 'boolean' } };

        allIncomingFields.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        allIncomingFields.unshift(Field5);
        allIncomingFields.unshift(Field3);
        allIncomingFields.unshift(Field2);

        return allIncomingFields;
    }

    dropAvailable(event: CdkDragDrop<Field[]>, selectedFieldsList: CdkDropList<Field[]>): void {
        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.returnGridFieldsToAvailableFields(this.model.mapFilterMenuOptions.selectedFields, this.model.mapFilterMenuOptions.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.mapFilterMenuOptions.selectedFields, this.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.mapFilterMenuOptions.selectedFields, this.model.availableFields);
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;

        this.model.mapFiltersForm.patchValue({
            selectedFields: selectedFieldsList.data,
        });
    }

    dropSelected(event: CdkDragDrop<Field[]>, selectedFieldsList: CdkDropList<Field[]>, app: App, project: Project): void {
        const previousSelectedFieldIds = this.model.mapFilterMenuOptions.selectedFields.map(field => field.id);

        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateFields(this.model.mapFilterMenuOptions.selectedFields, this.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableFields(this.model.mapFilterMenuOptions.selectedFields, this.model.availableFields);
        this._dragAndDropFieldSelectionUtilityService.currentAvailableFieldState = this.model.availableFields;

        const currentSelectedFieldIds = selectedFieldsList.data.map((field: any) => field.id || field);

        const newlyAddedFieldIds = currentSelectedFieldIds.filter(fieldId =>
            !previousSelectedFieldIds.includes(fieldId)
        );

        if (newlyAddedFieldIds.length > 0) {
            const fieldsValuesToFetch: Field[] = this.allFields.filter(field =>
                newlyAddedFieldIds.includes(field.id)
            );

            if (fieldsValuesToFetch.length > 0 && !Object.keys(this.model.mapFilterMenuOptions.filterValues).find((filterValue) => filterValue === fieldsValuesToFetch[0].id)) {
                this.getMapFilterValuesByProjectId(app, project, fieldsValuesToFetch);
            }
        }

        this.model.mapFiltersForm.patchValue({
            selectedFields: selectedFieldsList.data,
        });
    }

    fieldFilterChange(filterString: string): void {
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.fieldFilterChange(filterString);
    }

    unselectAll(): void {
        this.selectedFields = [];
        this.model.availableFields = [...this.allFields];
    }

    saveFilter(currentApp: App, currentProject: Project): void {
        this.saveMapFilters(currentApp, currentProject, this.model.mapFiltersForm.value);
    }

    setAllFields(allIncomingFields: Field[], siteAlias: string): void {
        allIncomingFields = this.setDefaultFilters(allIncomingFields, siteAlias);

        this.model.mapFilterMenuOptions.allFields = allIncomingFields;
        this.model.mapFilterMenuOptions = { ...this.model.mapFilterMenuOptions };

        const incomingFieldsById = new Map<string, Field>();
        for (const incomingField of allIncomingFields) {
            incomingFieldsById.set(incomingField.id, incomingField);
        }

        const refreshedSelectedFields: Field[] = [];
        for (const selectedField of this.selectedFields) {
            const incomingField = incomingFieldsById.get(selectedField.id);
            if (incomingField) {
                refreshedSelectedFields.push(incomingField);
            }
        }

        this.selectedFields = refreshedSelectedFields;
    }

    isModelLoading(bool: boolean): void {
        this.model.isLoading = bool;
    }
}
